import React, { useState, useEffect } from 'react';
import { Edit } from 'lucide-react';
import { ClipLoader } from 'react-spinners';
import { 
  getUserSubscriptions, 
  fetchMyProfiles,
  getReligions,
  getCastes,
  getOccupations,
  getMotherTongue,
  getCountries,
  getStates,
  getCities
} from '../utils/api';

export function ProfileContent({ onSectionChange }) {
  const [userInfo, setUserInfo] = useState({
    userId: localStorage.getItem('matrimonyUserId') || '',
    username: localStorage.getItem('matrimonyUserName') || '',
    education: localStorage.getItem('matrimonyUserEducation') || '',
    occupation: localStorage.getItem('matrimonyUserOccupation') || '',
    annualIncome: localStorage.getItem('matrimonyUserAnnualIncome') || '',
  });

  const [currentPlan, setCurrentPlan] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [options, setOptions] = useState({
    religions: [],
    castes: [],
    occupations: [],
    motherTongues: [],
    countries: [],
    states: [],
    cities: []
  });
  const [loading, setLoading] = useState({
    profile: true,
    plan: true,
    options: true
  });

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch options data first
        const [
          religions,
          castes,
          occupations,
          motherTongues,
          countries,
          states,
          cities
        ] = await Promise.all([
          getReligions(),
          getCastes(),
          getOccupations(),
          getMotherTongue(),
          getCountries(),
          getStates(),
          getCities()
        ]);

        setOptions({
          religions,
          castes,
          occupations,
          motherTongues,
          countries,
          states,
          cities
        });
        setLoading(prev => ({ ...prev, options: false }));

        // Then fetch profile and subscription data
        const [profileResponse, subscriptions] = await Promise.all([
          fetchMyProfiles(),
          getUserSubscriptions()
        ]);

        if (profileResponse?.success) {
          setProfileData(profileResponse.data);
          const userId = localStorage.getItem('matrimonyUserId');
          const userProfile = profileResponse.data.find(profile => profile.user === userId);
          
          if (userProfile) {
            // Update local storage with profile data
            localStorage.setItem('matrimonyUserEducation', userProfile.education || '');
            localStorage.setItem('matrimonyUserOccupation', userProfile.occupation || '');
            localStorage.setItem('matrimonyUserAnnualIncome', userProfile.annualIncome || '');
            setUserInfo(prev => ({
              ...prev,
              education: userProfile.education || '',
              occupation: userProfile.occupation || '',
              annualIncome: userProfile.annualIncome || ''
            }));
          }
        }

        if (subscriptions && subscriptions.length) {
          setCurrentPlan(subscriptions[0]);
        }

        setLoading(prev => ({ ...prev, profile: false, plan: false }));
      } catch (err) {
        console.error('Error fetching data:', err);
        setLoading(prev => ({ ...prev, profile: false, plan: false, options: false }));
      }
    };

    fetchAllData();
  }, []);

  const handleEditClick = () => {
    if (typeof onSectionChange === 'function') {
      onSectionChange('profile-listing');
    }
  };

  const getOptionName = (id, optionType) => {
    if (!id || !options[optionType]) return '—';
    const option = options[optionType].find(opt => opt._id === id);
    return option ? option.name || option.title : '—';
  };

  if (loading.profile || loading.options) {
    return (
      <div className="flex-1 p-8 bg-gray-50 flex items-center justify-center">
        <ClipLoader size={50} color="#3B82F6" />
      </div>
    );
  }

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Profile Settings</h2>
          </div>
          <div className="p-6">
            <div className="mb-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">{userInfo.username}</h3>
                <p className="text-sm text-gray-500">
                  {loading.plan
                    ? 'Loading plan...'
                    : currentPlan
                    ? `${currentPlan.title} | ${currentPlan.tenure} Days`
                    : 'Free User | N/A'}
                </p>
              </div>
              <button
                className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                onClick={handleEditClick}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            </div>

            {!profileData ? (
              <div className="text-center py-8 text-gray-500">
                No profile data found
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { label: 'Full Name', value: profileData.fullName || '—' },
                    { label: 'Date of Birth', value: profileData.dob ? new Date(profileData.dob).toLocaleDateString() : '—' },
                    { label: 'Gender', value: profileData.gender || '—' },
                    { label: 'Religion', value: getOptionName(profileData.religion, 'religions') },
                    { label: 'Caste', value: getOptionName(profileData.caste, 'castes') },
                    { label: 'Occupation', value: getOptionName(profileData.occupation, 'occupations') },
                    { label: 'Annual Income', value: profileData.annualIncome || '—' },
                    { label: 'Mother Tongue', value: getOptionName(profileData.motherTongue, 'motherTongues') },
                    { label: 'Marital Status', value: profileData.maritalStatus || '—' },
                    { label: 'Country', value: getOptionName(profileData.country, 'countries') },
                    { label: 'State', value: getOptionName(profileData.state, 'states') },
                    { label: 'City', value: getOptionName(profileData.city, 'cities') },
                    { label: 'Education', value: profileData.education || '—' },
                    { label: 'Address', value: profileData.address || '—' },
                    { label: 'About Me', value: profileData.description || '—', fullWidth: true },
                  ].map((item, index) => (
                    <div key={index} className={item.fullWidth ? 'col-span-full' : ''}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {item.label}
                      </label>
                      <p className="text-gray-900">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Display additional sections if data exists */}
                {profileData.is_entrepreneur && (
                  <div className="mt-8 border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Entrepreneur Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        { label: 'Company Name', value: profileData.company_name || '—' },
                        { label: 'Expected Revenue', value: profileData.expected_revenue || '—' },
                        { label: 'Founder Vision', value: profileData.founder_vision || '—', fullWidth: true },
                      ].map((item, index) => (
                        <div key={index} className={item.fullWidth ? 'col-span-full' : ''}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {item.label}
                          </label>
                          <p className="text-gray-900">
                            {item.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {profileData.images?.length > 0 && (
                  <div className="mt-8 border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Images</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {profileData.images.map((image, index) => (
                        <div key={index} className="relative aspect-square overflow-hidden rounded-lg">
                          <img
                            src={image}
                            alt={`Profile ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}